import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import BackToTop from '@/components/feature/BackToTop';
import { CityPageData, cityPages } from '@/mocks/cityPages';
import { aggregateRating } from '@/mocks/testimonials';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

interface Props {
  data: CityPageData;
}

const CityLandingPage = ({ data }: Props) => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE_URL}/${data.slug}#webpage`,
        "url": `${SITE_URL}/${data.slug}`,
        "name": data.metaTitle,
        "description": data.metaDescription,
        "isPartOf": { "@id": `${SITE_URL}/#website` },
        "about": { "@id": `${SITE_URL}/#business` },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Service Areas", "item": `${SITE_URL}/service-areas` },
            { "@type": "ListItem", "position": 3, "name": `Electronics Recycling ${data.city}`, "item": `${SITE_URL}/${data.slug}` }
          ]
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${SITE_URL}/${data.slug}#breadcrumb`,
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
          { "@type": "ListItem", "position": 2, "name": "Service Areas", "item": `${SITE_URL}/service-areas` },
          { "@type": "ListItem", "position": 3, "name": `Electronics Recycling ${data.city}`, "item": `${SITE_URL}/${data.slug}` }
        ]
      },
      {
        "@type": "LocalBusiness",
        "@id": `${SITE_URL}/${data.slug}#localbusiness`,
        "name": `Greenly Electronics Recycling — ${data.city}`,
        "description": data.metaDescription,
        "url": `${SITE_URL}/${data.slug}`,
        "telephone": "+12136001000",
        "email": "info@greenly.us",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": data.city,
          "addressRegion": data.state,
          "postalCode": data.zip,
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": data.lat,
          "longitude": data.lng
        },
        "areaServed": [
          { "@type": "City", "name": data.city },
          ...data.nearbyAreas.map((n) => ({ "@type": "City", "name": n }))
        ],
        "parentOrganization": { "@id": `${SITE_URL}/#business` },
        "priceRange": "Free–$$",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": aggregateRating.ratingValue,
          "reviewCount": aggregateRating.reviewCount,
          "bestRating": aggregateRating.bestRating,
          "worstRating": aggregateRating.worstRating
        },
        "openingHoursSpecification": [
          { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "08:00", "closes": "17:00" },
          { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "09:00", "closes": "14:00" }
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": `Electronics Recycling Services in ${data.city}, CA`,
          "itemListElement": data.services.map((s, i) => ({
            "@type": "Offer",
            "position": i + 1,
            "itemOffered": {
              "@type": "Service",
              "name": s.title,
              "description": s.desc,
              "url": `${SITE_URL}${s.path}`
            }
          }))
        }
      },
      {
        "@type": "FAQPage",
        "@id": `${SITE_URL}/${data.slug}#faq`,
        "mainEntity": data.faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      }
    ]
  };

  return (
    <>
      <title>{data.metaTitle}</title>
      <meta name="description" content={data.metaDescription} />
      <meta name="keywords" content={`electronics recycling ${data.city}, e-waste pickup ${data.city}, computer recycling ${data.city} CA, TV recycling ${data.city}, data destruction ${data.city}`} />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/${data.slug}`} />
      {/* City-specific geo meta tags for hyper-local SEO */}
      <meta name="geo.region" content="US-CA" />
      <meta name="geo.placename" content={`${data.city}, California`} />
      <meta name="geo.position" content={`${data.lat};${data.lng}`} />
      <meta name="ICBM" content={`${data.lat}, ${data.lng}`} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <Navbar />
      <main>

        {/* Hero */}
        <section className="relative bg-gray-900 pt-24 pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={data.heroImage}
              alt={`Electronics recycling in ${data.city}, CA`}
              className="w-full h-full object-cover object-center opacity-30"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/80 to-transparent"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: 'Home', path: '/' },
              { label: 'Service Areas', path: '/service-areas' },
              { label: `${data.city}` }
            ]} />
            <div className="max-w-2xl mt-8">
              <div className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                <i className="ri-map-pin-2-fill"></i>
                {data.region} — {data.city}, CA
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-5">
                {data.heroHeadline.split(data.city).map((part, i, arr) => (
                  i < arr.length - 1
                    ? <span key={i}>{part}<span className="text-[#74C69D]">{data.city}</span></span>
                    : <span key={i}>{part}</span>
                ))}
              </h1>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">{data.heroSubtext}</p>

              {/* Rating badge */}
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-6">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className="ri-star-fill text-yellow-400 text-xs"></i>
                  ))}
                </div>
                <span className="text-white text-xs font-bold">{aggregateRating.ratingValue}</span>
                <span className="text-gray-300 text-xs">({aggregateRating.reviewCount.toLocaleString()} reviews)</span>
              </div>

              <div className="flex flex-wrap gap-3">
                <a
                  href="tel:+12136001000"
                  className="inline-flex items-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-phone-fill"></i>(213) 600-1000
                </a>
                <Link
                  to="/pickup-request"
                  className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-truck-line"></i>Request Free Pickup
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section className="bg-[#1B6B3A] py-4">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 text-white text-xs md:text-sm">
              {[
                { icon: 'ri-award-line', text: 'CalRecycle CEWD-127531' },
                { icon: 'ri-shield-check-line', text: 'HIPAA Compliant' },
                { icon: 'ri-truck-line', text: 'Free Business Pickup' },
                { icon: 'ri-file-list-3-line', text: 'Certificate Provided' },
                { icon: 'ri-map-pin-line', text: '2 Drop-Off Locations' },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-2">
                  <i className={`${b.icon} text-[#74C69D]`}></i>
                  <span className="font-semibold">{b.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Intro + Why Us */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
              <div className="w-full lg:w-3/5">
                <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">About Our Service</div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-5 leading-tight">
                  Electronics Recycling &amp; E-Waste Pickup in <strong>{data.city}</strong>
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{data.intro}</p>
                <p className="text-gray-600 text-sm leading-relaxed">{data.whyUs}</p>
              </div>
              <div className="w-full lg:w-2/5 space-y-4">
                {/* Nearest drop-off */}
                <div className="bg-[#F4FAF7] rounded-2xl p-6 border border-[#1B6B3A]/10">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#1B6B3A] rounded-xl shrink-0">
                      <i className="ri-map-pin-2-fill text-white text-lg"></i>
                    </div>
                    <div>
                      <div className="font-extrabold text-gray-900 text-sm">Nearest Drop-Off Location</div>
                      <div className="text-[#1B6B3A] text-xs font-semibold mt-0.5">{data.nearestDropOff.distance}</div>
                    </div>
                  </div>
                  <div className="text-gray-700 text-sm font-semibold mb-1">{data.nearestDropOff.name}</div>
                  <div className="text-gray-500 text-xs mb-1">{data.nearestDropOff.address}</div>
                  <div className="text-gray-400 text-xs mb-3">Mon–Fri 8am–5pm · Sat 9am–2pm</div>
                  <a
                    href={data.nearestDropOff.mapLink}
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="inline-flex items-center gap-1.5 text-[#1B6B3A] font-semibold text-xs hover:underline cursor-pointer"
                  >
                    <i className="ri-direction-line"></i>Get Directions
                  </a>
                </div>

                {/* Nearby areas */}
                <div className="bg-white border border-gray-200 rounded-2xl p-5">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Also Serving Nearby</div>
                  <div className="flex flex-wrap gap-2">
                    {data.nearbyAreas.map((area) => (
                      <span key={area} className="bg-[#F4FAF7] text-[#1B6B3A] text-xs font-semibold px-3 py-1 rounded-full">
                        {area}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Quick call */}
                <a
                  href="tel:+12136001000"
                  className="flex items-center gap-3 bg-[#1B6B3A] hover:bg-[#2D8653] rounded-2xl p-5 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full shrink-0">
                    <i className="ri-phone-fill text-white text-lg"></i>
                  </div>
                  <div>
                    <div className="text-white font-extrabold text-sm">Call for {data.city} Pickup</div>
                    <div className="text-green-200 text-xs">(213) 600-1000 · Mon–Sat</div>
                  </div>
                  <i className="ri-arrow-right-line text-white ml-auto group-hover:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">What We Offer</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                Electronics Recycling Services in {data.city}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {data.services.map((s) => (
                <Link
                  key={s.path}
                  to={s.path}
                  className="bg-white rounded-xl p-6 border border-transparent hover:border-[#1B6B3A] transition-colors cursor-pointer group"
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-[#E8F5EE] group-hover:bg-[#1B6B3A] rounded-xl mb-4 transition-colors">
                    <i className={`${s.icon} text-[#1B6B3A] group-hover:text-white text-xl transition-colors`}></i>
                  </div>
                  <h3 className="font-extrabold text-gray-900 text-sm mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
                  <div className="mt-3 flex items-center gap-1 text-[#1B6B3A] text-xs font-bold">
                    Learn more <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Simple Process</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                How E-Waste Pickup Works in {data.city}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { num: '01', icon: 'ri-phone-line', title: 'Call or Request Online', desc: `Call (213) 600-1000 or submit a pickup request. Tell us your ${data.city} address and what you need recycled.` },
                { num: '02', icon: 'ri-calendar-check-line', title: 'We Schedule Pickup', desc: `Our team confirms your ${data.city} pickup date and time — usually within 1–3 business days.` },
                { num: '03', icon: 'ri-truck-line', title: 'We Come to You', desc: `Our certified crew arrives at your ${data.city} location, loads everything, and handles all the heavy lifting.` },
                { num: '04', icon: 'ri-file-list-3-line', title: 'You Get Documentation', desc: 'Receive a Certificate of Recycling and, if requested, individual device certificates for data destruction.' },
              ].map((step) => (
                <div key={step.num} className="bg-[#F4FAF7] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#1B6B3A] rounded-xl shrink-0">
                      <i className={`${step.icon} text-white text-lg`}></i>
                    </div>
                    <span className="text-3xl font-extrabold text-gray-200">{step.num}</span>
                  </div>
                  <h3 className="font-extrabold text-gray-900 text-sm mb-2">{step.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Common Questions</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                {data.city} Electronics Recycling FAQs
              </h2>
            </div>
            <div className="space-y-3">
              {data.faqs.map((faq, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="text-sm font-bold text-gray-900">{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className={`text-[#1B6B3A] transition-transform duration-200 ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Internal linking - Other city pages */}
        <section className="py-10 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-6">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Electronics Recycling Near {data.city}</div>
              <h2 className="text-lg font-extrabold text-gray-900">We Also Serve These {data.region} Cities</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {cityPages
                .filter((c) => c.slug && c.slug !== data.slug)
                .slice(0, 12)
                .map((city) => (
                  <Link
                    key={city.slug}
                    to={`/${city.slug}`}
                    className="flex items-center gap-2 bg-[#F4FAF7] hover:bg-[#E8F5EE] border border-transparent hover:border-[#1B6B3A] text-gray-700 hover:text-[#1B6B3A] px-4 py-3 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-map-pin-line text-xs"></i>
                    {city.city}
                  </Link>
                ))}
            </div>            <div className="text-center mt-6">
              <Link
                to="/service-areas"
                className="inline-flex items-center gap-2 text-[#1B6B3A] font-bold text-sm hover:underline cursor-pointer"
              >
                <i className="ri-map-2-line"></i>
                View All Service Areas
              </Link>
            </div>
          </div>
        </section>

        {/* Related cities - nearby areas */}
        <section className="py-10 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-3">Nearby Neighborhoods</div>
            <h2 className="text-lg font-extrabold text-gray-900 mb-5">Areas Near {data.city} We Also Serve</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {data.nearbyAreas.map((area) => (
                <span
                  key={area}
                  className="flex items-center gap-2 bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-full text-sm"
                >
                  <i className="ri-map-pin-line text-xs text-[#1B6B3A]"></i>
                  {area}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 bg-[#1B6B3A]">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
              Ready to Recycle Electronics in {data.city}?
            </h2>
            <p className="text-green-100 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Call us or request a free pickup online. We serve {data.city} and all of Los Angeles County — businesses, schools, hospitals, and residents.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:+12136001000"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#1B6B3A] font-bold px-8 py-3 rounded-md hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-phone-fill"></i>Call (213) 600-1000
              </a>
              <Link
                to="/pickup-request"
                className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-truck-line"></i>Request Free Pickup
              </Link>
              <Link
                to="/locations"
                className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-map-pin-line"></i>Find Drop-Off Location
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
};

export default CityLandingPage;
